import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  TimePicker,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCreateCrmActivity, useCrmActivityTypes, useUpdateCrmActivity } from '@/hooks/useCrmActivities';
import { useUsers } from '@/hooks/useUsers';
import { useAuthStore } from '@/store/authStore';
import DocumentUpload from '@/components/documents/DocumentUpload';
import type { CrmActivity, CrmActivityInput } from '@/services/crmActivity.service';
import CrmRecordPicker, { type CrmRecordKind, type CrmRecordLink } from './CrmRecordPicker';

const { TextArea } = Input;
const { Text } = Typography;

export interface CrmActivityLink {
  lead_id?: string | null;
  opportunity_id?: string | null;
  customer_id?: string | null;
  demo_id?: string | null;
}

interface Props {
  /** Record the activity belongs to. When omitted the user picks a customer, lead or opportunity. */
  link?: CrmActivityLink;
  pickerKinds?: CrmRecordKind[];
  defaultTypeCode?: string;
  /** Prefill the activity date and time (ISO), used when opening the form from the calendar. */
  defaultActivityDate?: string | null;
  submitLabel?: string;
  /** Existing row. When set, save updates it instead of creating another log. */
  activity?: CrmActivity | null;
  onSaved?: (row: CrmActivity) => void;
  onCancel?: () => void;
}

interface FormValues {
  record?: CrmRecordLink | null;
  activity_type_code: string;
  activity_date: Dayjs;
  duration_minutes?: number | null;
  contact_name?: string;
  subject?: string;
  description?: string;
  customer_requirements?: string;
  outcome?: string;
  sentiment?: 'Positive' | 'Neutral' | 'Negative';
  needs_follow_up?: boolean;
  follow_up_date?: Dayjs;
  follow_up_time?: Dayjs | null;
  follow_up_owner?: string;
  is_commitment?: boolean;
  commitment_text?: string;
  committed_to?: 'customer' | 'internal';
}

const CrmActivityForm: React.FC<Props> = ({
  link,
  pickerKinds,
  defaultTypeCode,
  defaultActivityDate,
  submitLabel = 'Log activity',
  activity,
  onSaved,
  onCancel,
}) => {
  const [form] = Form.useForm<FormValues>();
  const { user } = useAuthStore();
  const { data: types = [], isLoading: typesLoading } = useCrmActivityTypes();
  const { data: users = [] } = useUsers({ is_active: true });
  const createActivity = useCreateCrmActivity();
  const updateActivity = useUpdateCrmActivity();
  const isSystem = activity?.activity_type_code === 'SYSTEM';
  const [lastSaved, setLastSaved] = useState<CrmActivity | null>(null);

  const hasFixedLink = !!(link?.lead_id || link?.opportunity_id || link?.customer_id);
  const typeCode = Form.useWatch('activity_type_code', form);
  const needsFollowUp = Form.useWatch('needs_follow_up', form);
  const isCommitment = Form.useWatch('is_commitment', form);
  const selectedType = useMemo(() => types.find((t) => t.code === typeCode), [types, typeCode]);

  useEffect(() => {
    if (!defaultActivityDate || activity) return;
    const d = dayjs(defaultActivityDate);
    if (d.isValid()) form.setFieldValue('activity_date', d);
  }, [defaultActivityDate, form, activity]);

  useEffect(() => {
    if (!activity) return;
    const kind: CrmRecordKind | null = activity.opportunity_id
      ? 'opportunity'
      : activity.lead_id
        ? 'lead'
        : activity.customer_id
          ? 'customer'
          : null;
    const recordId = activity.opportunity_id || activity.lead_id || activity.customer_id;
    form.setFieldsValue({
      record: kind && recordId ? { kind, id: recordId } : null,
      activity_type_code: activity.activity_type_code,
      activity_date: dayjs(activity.activity_date),
      duration_minutes: activity.duration_minutes,
      contact_name: activity.contact_name ?? undefined,
      subject: activity.subject ?? undefined,
      description: activity.description ?? undefined,
      customer_requirements: activity.customer_requirements ?? undefined,
      outcome: activity.outcome ?? undefined,
      sentiment: activity.sentiment ?? undefined,
      needs_follow_up: !!activity.follow_up_date,
      follow_up_date: activity.follow_up_date ? dayjs(activity.follow_up_date) : undefined,
      follow_up_time: activity.follow_up_time ? dayjs(activity.follow_up_time, 'HH:mm:ss') : undefined,
      follow_up_owner: activity.follow_up_owner ?? undefined,
      is_commitment: activity.is_commitment,
      commitment_text: activity.commitment_text ?? undefined,
      committed_to: activity.committed_to ?? 'customer',
    });
  }, [activity, form]);

  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: u.id,
        label: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || u.id,
      })),
    [users]
  );

  const onFinish = async (v: FormValues) => {
    const recordLink: CrmActivityLink = hasFixedLink
      ? { ...link }
      : {
          lead_id: v.record?.kind === 'lead' ? v.record.id : null,
          opportunity_id: v.record?.kind === 'opportunity' ? v.record.id : null,
          customer_id: v.record?.kind === 'customer' ? v.record.id : null,
          demo_id: link?.demo_id ?? null,
        };
    const body: CrmActivityInput = {
      ...recordLink,
      activity_type_code: v.activity_type_code,
      activity_date: v.activity_date ? v.activity_date.toISOString() : undefined,
      duration_minutes: v.duration_minutes ?? null,
      contact_name: v.contact_name || null,
      subject: v.subject || null,
      description: v.description || null,
      customer_requirements: v.customer_requirements || null,
      outcome: v.outcome || null,
      sentiment: v.sentiment ?? null,
      follow_up_date: v.needs_follow_up && v.follow_up_date ? v.follow_up_date.format('YYYY-MM-DD') : null,
      follow_up_time: v.needs_follow_up && v.follow_up_time ? v.follow_up_time.format('HH:mm:ss') : null,
      follow_up_owner: v.needs_follow_up ? v.follow_up_owner || user?.id || null : null,
      is_commitment: !!(v.needs_follow_up && v.is_commitment),
      commitment_text: v.needs_follow_up && v.is_commitment ? v.commitment_text || null : null,
      committed_to: v.needs_follow_up && v.is_commitment ? v.committed_to ?? 'customer' : null,
    };
    if (activity) {
      const row = await updateActivity.mutateAsync({ id: activity.id, body });
      onSaved?.(row);
      return;
    }
    const row = await createActivity.mutateAsync(body);
    setLastSaved(row);
    form.resetFields();
    onSaved?.(row);
  };

  return (
    <>
      {isSystem && <Alert type="info" showIcon style={{ marginBottom: 12 }} message="System entries stay read-only." />}
      <Form<FormValues>
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          activity_type_code: defaultTypeCode,
          activity_date: dayjs(),
          committed_to: 'customer',
        }}
      >
        {activity ? (
          <Form.Item label="Customer / lead / opportunity">
            <Text>
              {activity.opportunity?.opportunity_name ||
                activity.lead?.customer_name ||
                activity.customer?.customer_name ||
                'Linked record'}
            </Text>
          </Form.Item>
        ) : (
          !hasFixedLink && (
            <Form.Item
              name="record"
              label="Customer / lead / opportunity"
              rules={[{ required: true, message: 'Choose who this activity was with' }]}
            >
              <CrmRecordPicker kinds={pickerKinds} />
            </Form.Item>
          )
        )}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item name="activity_type_code" label="Type" rules={[{ required: true, message: 'Choose a type' }]}>
              <Select
                loading={typesLoading}
                placeholder="Call, visit, meeting…"
                options={types.map((t) => ({ value: t.code, label: t.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="activity_date" label="Date & time" rules={[{ required: true }]}>
              <DatePicker showTime={{ format: 'HH:mm' }} format="DD-MM-YYYY HH:mm" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="contact_name" label="Met / spoke with">
              <Input placeholder="Contact name and role" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="duration_minutes" label="Duration (minutes)">
              <InputNumber min={0} max={1440} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="subject" label="Subject">
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item name="description" label="Notes / discussion">
          <TextArea rows={3} />
        </Form.Item>
        <Form.Item name="customer_requirements" label="Customer requirements">
          <TextArea rows={2} placeholder="What the customer asked for or needs" />
        </Form.Item>
        <Row gutter={12}>
          <Col xs={24} md={16}>
            <Form.Item
              name="outcome"
              label="Outcome"
              tooltip={selectedType?.requires_outcome ? 'Required for this type (or fill the notes)' : undefined}
            >
              <TextArea rows={2} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item name="sentiment" label="Customer sentiment">
              <Radio.Group optionType="button" buttonStyle="solid" size="small">
                <Radio.Button value="Positive">Positive</Radio.Button>
                <Radio.Button value="Neutral">Neutral</Radio.Button>
                <Radio.Button value="Negative">Negative</Radio.Button>
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="needs_follow_up" valuePropName="checked" style={{ marginBottom: 8 }}>
          <Checkbox>Follow-up needed</Checkbox>
        </Form.Item>
        {needsFollowUp && (
          <div style={{ padding: 12, background: 'rgba(0,0,0,0.02)', borderRadius: 6, marginBottom: 16 }}>
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="follow_up_date"
                  label="Follow-up date"
                  rules={[{ required: true, message: 'Pick the follow-up date' }]}
                >
                  <DatePicker format="DD-MM-YYYY" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="follow_up_time" label="Time" tooltip="Leave blank for an all-day follow-up">
                  <TimePicker format="HH:mm" style={{ width: '100%' }} minuteStep={15} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="follow_up_owner" label="Owner" tooltip="Defaults to you">
                  <Select allowClear showSearch optionFilterProp="label" options={userOptions} placeholder="Me" />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="is_commitment" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>This is a commitment</Checkbox>
            </Form.Item>
            {isCommitment && (
              <>
                <Form.Item
                  name="commitment_text"
                  label="What was promised"
                  rules={[{ required: true, message: 'Describe the commitment' }]}
                >
                  <TextArea rows={2} />
                </Form.Item>
                <Form.Item name="committed_to" label="Committed to">
                  <Radio.Group>
                    <Radio value="customer">Customer</Radio>
                    <Radio value="internal">Internal team</Radio>
                  </Radio.Group>
                </Form.Item>
              </>
            )}
          </div>
        )}

        <Space>
          <Button type="primary" htmlType="submit" loading={createActivity.isPending || updateActivity.isPending} disabled={isSystem}>
            {submitLabel}
          </Button>
          {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        </Space>
      </Form>

      {lastSaved && (
        <Alert
          style={{ marginTop: 12 }}
          type="success"
          showIcon
          closable
          onClose={() => setLastSaved(null)}
          message={
            <Space wrap>
              <Text>
                {lastSaved.activity_type_name} logged{lastSaved.subject ? `: ${lastSaved.subject}` : ''}.
              </Text>
              <DocumentUpload entityType="CrmActivity" entityId={lastSaved.id} buttonText="Attach file" />
            </Space>
          }
        />
      )}
    </>
  );
};

export default CrmActivityForm;
